import { useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { MapPin, X, AlertTriangle, CheckCircle2 } from "lucide-react";

const INDIA_TOPO_URL = "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@main/topojson/india.json";

// State name normalization for matching pincodes_master state names
const normalizeState = (name) => {
  if (!name) return "";
  return name.trim().toLowerCase()
    .replace(/&/g, "and")
    .replace(/\s+/g, " ");
};

const BREACH_COLORS = [
  { min: 0, max: 0, color: "#E8F5E9", label: "No Breaches" },
  { min: 1, max: 2, color: "#FFE0B2", label: "1-2 Breaches" },
  { min: 3, max: 5, color: "#FFAB91", label: "3-5 Breaches" },
  { min: 6, max: 10, color: "#EF5350", label: "6-10 Breaches" },
  { min: 11, max: Infinity, color: "#B71C1C", label: "11+ Breaches" },
];

const getBreachColor = (count) => {
  if (count === undefined || count === null) return "#F5F5F5";
  for (const tier of BREACH_COLORS) {
    if (count >= tier.min && count <= tier.max) return tier.color;
  }
  return "#B71C1C";
};

const IndiaMap = ({ breachData, pincodeDetails, onStateClick }) => {
  const [tooltip, setTooltip] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // breachData: { "Maharashtra": 5, "Delhi": 3, ... }
  // pincodeDetails: { "Maharashtra": [{ pincode, product, platform, price, decided_price, breach }], ... }

  const handleStateClick = (stateName) => {
    setSelectedState(stateName === selectedState ? null : stateName);
    if (onStateClick) onStateClick(stateName);
  };

  const stateBreaches = useMemo(() => breachData || {}, [breachData]);
  const totalBreaches = useMemo(() => Object.values(stateBreaches).reduce((a, b) => a + b, 0), [stateBreaches]);

  return (
    <div className="relative" data-testid="india-map">
      <div className="flex gap-6">
        {/* Map */}
        <div className="flex-1 relative bg-gradient-to-b from-slate-50 to-white rounded-xl border border-gray-200 overflow-hidden" style={{ minHeight: 420 }}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 1000, center: [82, 22] }}
            width={600}
            height={500}
            style={{ width: "100%", height: "auto" }}
          >
            <ZoomableGroup>
              <Geographies geography={INDIA_TOPO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const stateName = geo.properties.ST_NM || geo.properties.NAME_1 || geo.properties.name || "";
                    const normalized = normalizeState(stateName);
                    // Match breach count
                    const breachCount = Object.entries(stateBreaches).find(
                      ([k]) => normalizeState(k) === normalized
                    )?.[1] || 0;
                    const isSelected = normalizeState(selectedState) === normalized;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isSelected ? "#32B4AC" : getBreachColor(breachCount)}
                        stroke="#fff"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: "none" },
                          hover: { fill: isSelected ? "#2A9D96" : "#B2DFDB", outline: "none", cursor: "pointer" },
                          pressed: { outline: "none" },
                        }}
                        onMouseEnter={() => setTooltip({ name: stateName, breaches: breachCount })}
                        onMouseLeave={() => setTooltip(null)}
                        onClick={() => handleStateClick(stateName)}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {/* Tooltip */}
          {tooltip && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-lg pointer-events-none z-10">
              <div className="text-xs font-semibold text-gray-900">{tooltip.name}</div>
              <div className={`text-[11px] font-medium mt-0.5 ${tooltip.breaches > 0 ? "text-red-600" : "text-green-600"}`}>
                {tooltip.breaches > 0 ? `${tooltip.breaches} breach${tooltip.breaches > 1 ? "es" : ""}` : "Compliant"}
              </div>
            </div>
          )}
        </div>

        {/* Legend + State Detail Panel */}
        <div className="w-56 shrink-0 space-y-4">
          {/* Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">Breach Summary</div>
            <div className={`text-2xl font-bold ${totalBreaches > 0 ? "text-red-600" : "text-green-600"}`}>{totalBreaches}</div>
            <div className="text-[11px] text-gray-500">Total breaches found</div>
          </div>

          {/* Legend */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-3">Legend</div>
            <div className="space-y-2">
              {BREACH_COLORS.map((tier) => (
                <div key={tier.label} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: tier.color }} />
                  <span className="text-[11px] text-gray-600">{tier.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Selected State Pincode Drill-down */}
          {selectedState && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-[#32B4AC]" />
                  {selectedState}
                </div>
                <button onClick={() => setSelectedState(null)} className="p-0.5 rounded hover:bg-gray-100">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              </div>
              {pincodeDetails && pincodeDetails[selectedState]?.length > 0 ? (
                <div className="space-y-1.5">
                  {pincodeDetails[selectedState].map((item, i) => (
                    <div key={i} className={`text-[10px] p-2 rounded-lg border ${item.breach ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        {item.breach ? (
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                        )}
                        <span className="font-mono font-medium text-gray-700">{item.pincode}</span>
                        <span className={`ml-auto px-1 py-0.5 rounded text-[9px] font-medium ${item.platform === "amazon" ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-700"}`}>
                          {item.platform}
                        </span>
                      </div>
                      <div className="text-gray-600 truncate">{item.product}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={item.breach ? "text-red-600 font-bold" : "text-gray-700"}>₹{item.price}</span>
                        {item.decided_price && (
                          <span className="text-gray-400">MAP: ₹{item.decided_price}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-gray-400 text-center py-4">No pincodes in this state</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndiaMap;
